import React, { useEffect, useState } from 'react';
import {
  App, Button, Form, Switch, Tag,
} from 'antd';
import { ExclamationCircleFilled, SaveOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { useSimpleModal } from '../../../Common/Modal/Simple';
import { useMessageError } from '../../../../hooks/common';
import SinglePageContainer from '../../../Common/SinglePageContainer';
import {
  UserCreateParams,
  UserTableRow,
  UserUpdateParams,
  useUserChangeEmails,
  useUserGetById,
  useUserUpdate,
} from '../../../../hooks/api/users';
import {
  DeactivateButton,
  DeleteButton,
  InviteUserButton,
  ResetPasswordButton,
} from '../actions';
import SuperAdminAccessButton from '../SuperAdminAccessButton';
import { useUnsavedChanges } from '../../../../context/unsavedChanges';
import { isObjEqual } from '../../../../utils';
import { useAuth, User } from '../../../../store/auth';
import { UserRole } from '../../../../enums/user';
import UserFormItems from '../Form';
import { getUserStatusColor } from '../index';
import BusinessPartners from './BusinessPartners';

const initialState: UserUpdateParams = {
  // Partial<User>
  // firstName: '',
  // dateOfBirth: dayjs(),
  // phone: '',
  status: 'active',
  email: '',
  role: UserRole.GUEST,
};

const prepareValues = (data: User): UserCreateParams => {
  const {
    status,
    email,
    role, // firstName, phone, dateOfBirth,
  } = data;

  return {
    // firstName,
    // dateOfBirth: dateOfBirth ? dayjs(dateOfBirth) : null,
    // phone,
    status,
    email,
    role,
  };
};

export default function UserView(): React.ReactNode {
  const { id = '' } = useParams();
  const { message } = App.useApp();
  const navigate = useNavigate();
  const { open, contextHolder } = useSimpleModal();
  const [form] = Form.useForm<UserUpdateParams>(); // UserUpdateParams
  const { user } = useAuth();
  const isReadOnly = user?.role === UserRole.MITARBEITER;

  const userById = useUserGetById(id);
  const userUpdate = useUserUpdate(id); // pageKey
  const userChangeEmails = useUserChangeEmails(id); // pageKey

  const extendedUserRes: UserTableRow | null = userById.data ? {
    key: userById.data.id,
    fullName: `${userById.data.firstName}`,
    ...userById.data,
  } : null;

  /** Initial Values >>> */
  const { unsavedChanges, handleUnsavedChanges } = useUnsavedChanges();
  const [initialValues, setInitialValues] = useState(initialState); // <User>
  const [triggerRefresh, setTriggerRefresh] = useState<
    string | number | undefined
  >();

  useEffect(() => {
    if (userById.data && !userById.loading && !userById.error) {
      setInitialValues(prepareValues(userById.data));
    }
  }, [userById.data]);

  useEffect(() => {
    form.resetFields();
    form.setFieldsValue(initialValues);
    handleUnsavedChanges(false);
  }, [initialValues]);
  /** <<< Initial Values */

  const handleSubmit = (values: UserUpdateParams) => {
    form.validateFields().then(({ email, ...val }) => {
      const regularUserUpdate = () => userUpdate
        .fetch(
          {
            ...val,
            status: undefined,
          },
          id,
        )
        .then((res) => {
          if (res?.id) {
            message.success('Changes saved successfully');
            setInitialValues(prepareValues(res));
            setTriggerRefresh(Date.now());
          }
        });

      if (email !== initialValues.email) {
        open({
          icon: <ExclamationCircleFilled />,
          title: 'Change email?',
          content: (
            <span>
              Are you sure you want to update the email for all business
              partners associated with
              {' '}
              <b>
                {`${userById.data?.firstName || '-'} ${
                  userById.data?.lastName || '-'
                }`}
              </b>
              ?
            </span>
          ),
          cancelText: 'Cancel',
          okText: 'Save and send confirmation email',
          okButtonProps: {
            danger: true,
          },
          onOk: () => {
            handleUnsavedChanges(false);
            userChangeEmails.fetch({ email }).then(regularUserUpdate);
          },
        });
      } else {
        handleUnsavedChanges(false);
        regularUserUpdate();
      }
    });
  };

  useMessageError([userById, userUpdate, userChangeEmails]);

  const handleFieldsChange = () => {
    if (!initialValues) return;
    handleUnsavedChanges(!isObjEqual(initialValues, form.getFieldsValue()));
  };

  return (
    <SinglePageContainer
      title={isReadOnly ? 'View user' : 'Edit user'}
      breadcrumbItems={[]}
      stickyHeader
      cardLoading={userById.loading}
      extra={(
        <>
          {initialValues.status ? (
            <Tag
              color={getUserStatusColor(initialValues.status)}
              className="capitalize-first-letter"
            >
              {initialValues.status}
            </Tag>
          ) : null}
          {extendedUserRes && !isReadOnly ? ( // userById.data
            <>
              <SuperAdminAccessButton
                record={extendedUserRes}
                buttonProps={{ type: 'default' }}
              />
              <InviteUserButton
                record={extendedUserRes}
                buttonProps={{ type: 'default' }}
              />
              <ResetPasswordButton
                record={extendedUserRes}
                buttonProps={{ type: 'default', children: 'Reset password' }}
                afterSuccess={(res) => (res ? setInitialValues(prepareValues(res)) : undefined)}
              />
              <DeactivateButton
                record={{ ...extendedUserRes, status: initialValues.status || extendedUserRes.status }}
                buttonProps={{ type: 'default' }}
                afterSuccess={(res) => (res ? setInitialValues(prepareValues(res)) : undefined)}
              />
              <DeleteButton
                record={extendedUserRes}
                buttonProps={{ type: 'default' }}
                afterSuccess={() => navigate('/users')}
              />
            </>
          ) : null}
          {!isReadOnly && (
            <Button
              type="primary"
              onClick={form.submit}
              icon={<SaveOutlined />}
              loading={
                userUpdate.loading || userChangeEmails.loading || userById.loading
              }
              disabled={!unsavedChanges}
            >
              Save
            </Button>
          )}
        </>
      )}
    >
      {contextHolder}
      <Form
        form={form}
        name="user-form"
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={initialValues}
        onFieldsChange={handleFieldsChange}
        disabled={isReadOnly}
      >
        <UserFormItems data={initialValues} />
      </Form>

      <div style={{ display: 'flex', gap: '16px', marginBottom: '16px' }}>
        <div>
          <p style={{ margin: 0 }}>
            <b>Has mobile app:</b>
          </p>
          <Switch
            value={!!userById.data?.hasMobileApp}
            checkedChildren="Yes"
            unCheckedChildren="No"
            disabled
          />
        </div>
        <div>
          <p style={{ margin: 0 }}>
            <b>Is private:</b>
          </p>
          <Switch
            value={!!userById.data?.isPrivate || false}
            checkedChildren="Yes"
            unCheckedChildren="No"
            disabled
          />
        </div>
      </div>

      <BusinessPartners triggerRefresh={triggerRefresh} />
    </SinglePageContainer>
  );
}
